<script setup lang="ts">
/**
 * The application chrome: brand, project context, page context, user menu.
 *
 * Rendered by `App.vue` only when there is a session, so the login and 404
 * views appear bare rather than inside a frame that implies one.
 *
 * ONE 52px bar for the whole app. The editor used to stack a second header of
 * its own under this one, which cost the canvas ~90px of the screen that has
 * the least room to spare. Instead the bar carries two mount points and a
 * route teleports its breadcrumb and its actions into them, so the page owns
 * its content without owning a bar.
 */
import { LogOut } from '@lucide/vue'
import { computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { useSession } from '../../../auth/session.js'
import { useAppStore } from '../stores/app.store.js'
import { ALL_PROJECTS, useProjectStore } from '../stores/project.store.js'
import { UiFilterSelect, UiToast, type FilterOption } from './ui/index.js'

const { t } = useI18n()
const router = useRouter()
const session = useSession()
const app = useAppStore()
const projectStore = useProjectStore()

const projectOptions = computed<FilterOption[]>(() => [
  // First and always present: it is a context, not a filter, so it belongs in
  // the same list as the projects rather than behind the clear button — which
  // this selector does not even offer (`:clearable="false"`).
  { value: ALL_PROJECTS, label: t('common.project.all'), hint: t('common.project.allHint') },
  ...projectStore.projects.map((project) => ({
    value: String(project.id),
    label: project.name,
    // The plant's own timezone decides when a periodic flow's period starts,
    // so it belongs next to the project's name rather than buried in a form.
    hint: project.localTimezone,
  })),
])

const selectedProject = computed(() =>
  projectStore.selection === null ? null : String(projectStore.selection),
)

function onProjectChange(value: string | null): void {
  if (value === null) projectStore.select(null)
  else projectStore.select(value === ALL_PROJECTS ? ALL_PROJECTS : Number(value))
}

async function logout(): Promise<void> {
  await session.logout()
  await router.push({ name: 'login' })
}

onMounted(() => {
  void app.load()
  void projectStore.load()
})
</script>

<template>
  <div class="flex min-h-dvh flex-col bg-base-200">
    <header class="border-hairline bg-base-100 flex h-[52px] shrink-0 items-center border-b px-4">
      <div class="flex w-60 shrink-0 items-center gap-2.5">
        <!-- The brand is also the way back to the listing: the bar has no
             navigation row any more, and the breadcrumb only exists on routes
             that teleport one. -->
        <RouterLink
          :to="{ name: 'flows' }"
          class="flex items-center gap-2.5"
          :title="t('common.nav.flows')"
        >
          <span
            class="rounded-selector bg-primary text-primary-content grid size-[22px] place-items-center text-[13px] font-extrabold"
          >
            V
          </span>
          <span class="text-base-content text-[15px] font-bold tracking-[.2px]">
            {{ t('common.appName') }}
          </span>
        </RouterLink>

        <!-- Not decoration: dev, test and production are visually identical,
             and publishing from Vulcan writes into a live engine. -->
        <span
          v-if="app.showStage"
          class="text-primary border-primary/45 rounded-[3px] border px-1.5 py-px text-[10px] font-semibold tracking-[.8px] uppercase"
          :title="t('common.stage.hint')"
        >
          {{ app.stage }}
        </span>
      </div>

      <!-- Both mount points stay in the tree with no page teleporting into
           them: empty, they contribute no height, and the bar keeps its
           geometry on the routes that have nothing to say here. -->
      <div id="topbar-crumbs" class="flex min-w-0 flex-1 items-center gap-2"></div>

      <div class="flex shrink-0 items-center gap-2.5">
        <div id="topbar-actions" class="flex items-center gap-1.5"></div>

        <!-- The dot and the 30px height are the bar's scale, and the kit's
             filter control cannot express either through its props. Reaching
             into it from the wrapper keeps one select implementation instead
             of a forked copy that drifts. -->
        <div
          class="relative flex items-center [&_[role=combobox]]:h-[30px] [&_[role=combobox]]:min-h-[30px] [&_[role=combobox]]:pl-7 [&_[role=combobox]]:text-[12.5px]"
        >
          <span
            class="bg-success pointer-events-none absolute left-2.5 z-10 size-1.5 rounded-full"
          ></span>
          <UiFilterSelect
            :model-value="selectedProject"
            :options="projectOptions"
            :placeholder="t('common.project.placeholder')"
            :clearable="false"
            :disabled="projectStore.projects.length === 0"
            width-class="w-48"
            @update:model-value="onProjectChange"
          />
        </div>

        <div class="dropdown dropdown-end">
          <button
            type="button"
            class="text-ink-soft hover:bg-white/5 hover:text-base-content flex h-[30px] max-w-44 items-center rounded-field px-2.5 text-[12.5px]"
            :aria-label="t('common.user.menu')"
          >
            <span class="truncate">{{ session.fullName }}</span>
          </button>
          <div
            tabindex="0"
            class="dropdown-content border-hairline-strong bg-base-100 z-40 mt-1 w-64 rounded-box border p-3"
          >
            <p class="truncate text-[12.5px] font-medium">{{ session.fullName }}</p>
            <p class="text-ink-subtle truncate text-[11.5px]">{{ session.email }}</p>
            <!-- Surfacing read-only up front beats letting someone build a
                 graph and discover at publish time that they may not. -->
            <p v-if="!session.canWrite" class="text-ink-muted mt-2 text-[11.5px]">
              {{ t('common.user.readOnly') }}
            </p>
            <button
              type="button"
              class="text-ink-soft hover:bg-white/5 hover:text-base-content mt-3 flex h-[30px] w-full items-center gap-2 rounded-field px-2.5 text-[12.5px]"
              @click="logout"
            >
              <LogOut class="size-4" />
              {{ t('common.user.logout') }}
            </button>
          </div>
        </div>
      </div>
    </header>

    <main class="min-h-0 flex-1">
      <slot />
    </main>

    <UiToast />
  </div>
</template>
